// Composant Menu Catalogue Particuliers - Style ManoMano
// Navigation par type (Acheter/Échanger/Louer) puis catégories et sous-catégories

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronRight, ChevronLeft, Menu,
  ShoppingCart, RefreshCw, Key,
  Wrench, Zap, Hammer, PanelTop, Paintbrush, Waves, TreeDeciduous, 
  Truck, Package, Construction, Settings, Lightbulb, Thermometer, 
  Home, TreePine, Droplets, Sun, Layers
} from 'lucide-react';
import { CATEGORIES_PARTICULIERS } from '../../data/categories-particuliers';

// Types d'annonces
const TYPES_ANNONCES = [
  { id: 'acheter', name: 'Acheter', icon: ShoppingCart, color: '#22C55E', description: 'Trouvez des bonnes affaires' },
  { id: 'echanger', name: 'Échanger', icon: RefreshCw, color: '#3B82F6', description: 'Échangez entre particuliers' },
  { id: 'louer', name: 'Louer', icon: Key, color: '#F97316', description: 'Location entre particuliers' },
];

// Mapping des icônes par nom
const ICONS_MAP = {
  'Construction': Construction,
  'Zap': Zap,
  'Hammer': Hammer,
  'PanelTop': PanelTop,
  'Paintbrush': Paintbrush,
  'Waves': Waves,
  'TreeDeciduous': TreeDeciduous,
  'Truck': Truck,
  'Package': Package,
  'Settings': Settings,
  'Lightbulb': Lightbulb,
  'Thermometer': Thermometer,
  'Home': Home,
  'TreePine': TreePine,
  'Droplets': Droplets,
  'Sun': Sun,
  'Layers': Layers,
  'Wrench': Wrench,
};

export default function ParticuliersCatalogueMenu({ isOpen, onClose, sidebarWidth }) {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCategorie, setSelectedCategorie] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset selections when closing
      setSelectedType(null);
      setSelectedCategorie(null);
    }
  }, [isOpen]);

  const handleTypeClick = (type) => {
    setSelectedType(type);
    setSelectedCategorie(null);
  };

  const handleCategorieClick = (categorie) => {
    if (categorie.sousCategories && categorie.sousCategories.length > 0) {
      setSelectedCategorie(categorie);
    } else {
      navigate(`/particuliers?type=${selectedType.id}&categorie=${categorie.id}`);
      onClose();
    }
  };

  const handleSousCategorieClick = (sousCategorie) => {
    navigate(`/particuliers?type=${selectedType.id}&categorie=${selectedCategorie.id}&sous_categorie=${sousCategorie.id}`);
    onClose();
  };

  const handleBack = () => {
    if (selectedCategorie) {
      setSelectedCategorie(null);
    } else if (selectedType) {
      setSelectedType(null);
    }
  };

  // Titre du header selon le niveau
  const getHeaderTitle = () => {
    if (selectedCategorie) return selectedCategorie.name;
    if (selectedType) return `${selectedType.name} - Catégories`;
    return 'Petites annonces';
  };

  const getIcon = (iconName) => {
    return ICONS_MAP[iconName] || Package;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 md:left-[288px] z-[9999] bg-black/50"
        style={sidebarWidth !== undefined ? { left: sidebarWidth } : {}}
        onClick={onClose}
      >
        {/* Panel menu */}
        <motion.div
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          exit={{ x: '-100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed inset-y-0 left-0 md:left-[288px] w-full max-w-md bg-white shadow-2xl overflow-hidden flex flex-col"
          style={sidebarWidth !== undefined ? { left: sidebarWidth } : {}}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between p-4 border-b"
            style={{ 
              background: selectedType 
                ? `linear-gradient(135deg, ${selectedType.color}, ${selectedType.color}dd)` 
                : 'linear-gradient(135deg, #1E3A5F, #2D4A6F)' 
            }}
          >
            <div className="flex items-center gap-3">
              {(selectedType || selectedCategorie) ? (
                <button
                  onClick={handleBack}
                  className="p-2 hover:bg-white/20 rounded-lg text-white"
                >
                  <ChevronLeft size={20} />
                </button>
              ) : (
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Menu size={20} className="text-white" />
                </div>
              )}
              <h2 className="text-lg font-bold text-white">
                {getHeaderTitle()}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg text-white"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Niveau 1: Types d'annonces (Acheter/Échanger/Louer) */}
            {!selectedType && (
              <div className="p-4">
                <p className="text-sm text-gray-500 mb-4">
                  Que souhaitez-vous faire ?
                </p>
                <div className="space-y-3">
                  {TYPES_ANNONCES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => handleTypeClick(type)}
                        className="w-full flex items-center gap-4 p-4 rounded-xl border-2 hover:border-gray-300 transition-all group"
                        style={{ borderColor: `${type.color}40` }}
                      >
                        <div 
                          className="w-14 h-14 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${type.color}15` }}
                        >
                          <Icon size={28} style={{ color: type.color }} />
                        </div>
                        <div className="flex-1 text-left">
                          <span className="font-semibold text-gray-900 text-lg">{type.name}</span>
                          <p className="text-sm text-gray-500">{type.description}</p>
                        </div>
                        <ChevronRight size={20} className="text-gray-400 group-hover:text-gray-600" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Niveau 2: Catégories */}
            {selectedType && !selectedCategorie && (
              <div className="p-2">
                {CATEGORIES_PARTICULIERS.map((categorie) => {
                  const Icon = getIcon(categorie.icon);
                  return (
                    <button
                      key={categorie.id}
                      onClick={() => handleCategorieClick(categorie)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-all group"
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${categorie.color}15` }}
                      >
                        <Icon size={20} style={{ color: categorie.color }} />
                      </div>
                      <span className="flex-1 text-left font-medium text-gray-800">
                        {categorie.name}
                      </span>
                      {categorie.sousCategories && categorie.sousCategories.length > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">
                            {categorie.sousCategories.length}
                          </span>
                          <ChevronRight size={18} className="text-gray-400 group-hover:text-gray-600" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Niveau 3: Sous-catégories */}
            {selectedCategorie && (
              <div className="p-2">
                {/* Option "Voir tout" */}
                <button
                  onClick={() => {
                    navigate(`/particuliers?type=${selectedType.id}&categorie=${selectedCategorie.id}`);
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-all mb-2"
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${selectedType.color}15` }}
                  >
                    {(() => {
                      const Icon = getIcon(selectedCategorie.icon);
                      return <Icon size={20} style={{ color: selectedType.color }} />;
                    })()}
                  </div>
                  <span className="flex-1 text-left font-semibold" style={{ color: selectedType.color }}>
                    Voir tout "{selectedCategorie.name}"
                  </span>
                  <ChevronRight size={18} style={{ color: selectedType.color }} />
                </button>

                <div className="h-px bg-gray-200 my-2" />

                {/* Liste des sous-catégories */}
                {selectedCategorie.sousCategories?.map((sousCategorie) => (
                  <button
                    key={sousCategorie.id}
                    onClick={() => handleSousCategorieClick(sousCategorie)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-all group"
                  >
                    <span className="flex-1 text-left text-gray-700">
                      {sousCategorie.name}
                    </span>
                    <ChevronRight size={16} className="text-gray-400 group-hover:text-gray-600" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-gray-50">
            <button
              onClick={() => {
                navigate('/particuliers/deposer');
                onClose();
              }}
              className="w-full py-3 px-4 rounded-xl font-semibold text-white transition-all"
              style={{ backgroundColor: '#F97316' }}
            >
              Déposer une annonce gratuite
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
